import { useState, useEffect, useCallback } from "react";

/**
 * @param {{
 *   open: boolean,
 *   workspaceName: string,
 *   onClose: () => void,
 *   onConfirm: () => Promise<void>,
 * }} props
 */
export default function DeleteWorkspaceModal({ open, workspaceName, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setFormError("");
  }, [open]);

  const handleConfirm = useCallback(async () => {
    setFormError("");
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Could not delete workspace.");
      setBusy(false);
    }
  }, [onConfirm, onClose]);

  if (!open) return null;

  const label = (workspaceName || "").trim() || "this workspace";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="ui-card-static flex w-full max-w-md flex-col overflow-hidden !p-0 sm:!p-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-ws-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-100 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="delete-ws-title" className="text-lg font-semibold tracking-tight text-slate-900">
                Delete workspace
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                This permanently removes the workspace and its Keycloak client. Members will no longer
                be able to switch to it.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ui-btn-icon shrink-0"
              aria-label="Close"
              disabled={busy}
            >
              <i className="ri-close-line text-xl" aria-hidden />
            </button>
          </div>
        </div>

        <div className="space-y-4 px-6 py-4">
          {formError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {formError}
            </div>
          ) : null}
          <p className="text-sm text-slate-700">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-slate-900">{label}</span>? This cannot be undone.
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button type="button" className="ui-btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 disabled:opacity-60"
            disabled={busy}
            onClick={() => void handleConfirm()}
          >
            {busy ? "Deleting…" : "Delete workspace"}
          </button>
        </div>
      </div>
    </div>
  );
}
